import { action, computed, observable, reaction } from 'mobx'
import { Types } from 'legend-builder/data/Type'

const COLLECTED_VALUES_KEY = 'previewer.organizerCollectedValues'

/**
 * Personal organizer inputs part of the previewer. Lets the user enter the
 * values the organizer would have collected.
 */
export default class OrganizerInputs {
  /**
   * Collected value answers, one per collect binding. We are reusing the
   * questions infrastructure.
   */
  @observable collectedValueInputs = []

  /**
   * Constructor.
   *
   * @param legendEditor The Legend Editor.
   */
  constructor(legendEditor) {
    this.legendEditor = legendEditor
    // Whenever the inputs change, write them to storage.
    reaction(
      () => this.collectedValueInputs.slice(),
      (collectedValueInputs) =>
        this.legendEditor.storage.setItem(
          this._makeCollectedValuesKey(),
          collectedValueInputs
        ),
      { delay: 500 }
    )
  }

  /**
   * Collected value elements in the legend that have a known binding.
   */
  @computed
  get collectedValueElements() {
    return this.legendEditor.questionsBuilder.allQuestions.filter(
      (q) => q.type === 'PersonalOrganizerCollectedValue' && q.binding
    )
  }

  /**
   * Maps the collected value elements to inputs for the questions editor,
   * which renders the previewer input rows.
   * Each input is keyed by its binding ID, because the organizer sends one
   * value per binding, and binding IDs don't change when an element ID does.
   */
  @computed
  get collectedValuesAsQuestions() {
    return this.collectedValueElements.map((element) =>
      mapBindingToQuestion(element.binding, element.id)
    )
  }

  /**
   * Mapped collected values for the compute payload. Answers for bindings
   * that are no longer in the legend are left out.
   * Compared structurally, so that editing the legend only changes the
   * compute payload (and triggers a compute) when the sent values change.
   */
  @computed.struct
  get mappedCollectedValues() {
    const bindings = this.collectedValueElements.map((e) => e.binding)
    return this.collectedValueInputs.flatMap((input) => {
      const binding = bindings.find((b) => b.id === input.id)
      if (!binding) {
        return []
      }

      return [
        {
          collect_binding_id: binding.id,
          value: parseAnswer(input.answer, binding.valueType),
        },
      ]
    })
  }

  /**
   * Replaces the collected value inputs with the specified value.
   */
  @action.bound
  setCollectedValueInputs(value) {
    this.collectedValueInputs = value
  }

  /**
   * Clears the collected value inputs.
   */
  @action.bound
  clearCollectedValueInputs() {
    this.setCollectedValueInputs([])
  }

  /**
   * Activates the inputs state.
   */
  @action.bound
  activate() {
    this.collectedValueInputs =
      this.legendEditor.storage.getItem(this._makeCollectedValuesKey()) || []
  }

  /**
   * Makes the collected values key for local storage.
   */
  _makeCollectedValuesKey() {
    return `${COLLECTED_VALUES_KEY}.${this.legendEditor.legendVersion.id}`
  }
}

/**
 * Maps a collect binding to a question for the questions editor.
 * Lists are entered as comma-separated text.
 *
 * @param binding
 * @param elementId
 */
function mapBindingToQuestion(binding, elementId) {
  const question = `${binding.displayName} (${elementId})`
  const valueType = binding.valueType
  if (Types.isNumberType(valueType)) {
    return {
      id: binding.id,
      type: 'NUMERIC',
      question,
      format: { type: 'Decimal', places: 2 },
    }
  }

  if (Types.isBoolType(valueType)) {
    return { id: binding.id, type: 'BOOL', question }
  }

  if (Types.isListType(valueType)) {
    return {
      id: binding.id,
      type: 'TEXT',
      question,
      description: 'Comma-separated list.',
    }
  }

  return { id: binding.id, type: 'TEXT', question }
}

/**
 * Parses an answer to the binding's value type. Lists are parsed from
 * comma-separated text.
 *
 * @param answer
 * @param valueType
 */
function parseAnswer(answer, valueType) {
  if (!Types.isListType(valueType)) {
    return answer
  }

  const items = String(answer ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
  if (Types.isNumberType(valueType.elementType)) {
    return items.map(Number)
  }

  return items
}
